import Backbone from 'backbone'
import _ from 'lodash'
import $ from 'jquery'

import {
	setPermissionsForGroupType
} from 'scoreboard/admin/principals/groups/advanced-permissions-form'
import BootslideMenu from 'scoreboard/components/bootslide/bootslide-menu'
import i18n from 'scoreboard/components/i18n'
import LoadingDots from 'scoreboard/components/loading-dots'
import Tooltip from 'scoreboard/components/tooltip'
import unwrapModel from 'scoreboard/components/unwrap-model'
import * as adminLink from 'scoreboard/components/admin-link'

import template from './organization-template-permissions-panel.hbs'
import OrganizationTemplatePermissionView from './organization-template-permission-view'
import OrganizationTemplatePermission from './organization-template-permission'

import DataType from 'scoreboard/datasets/model/data-type'
const { ROLLUP } = DataType
import groupType from '../admin/principals/groups/group-type'

const OrganizationTemplatePermissionsPanel = Backbone.View.extend({
	className: 'panel organization-template-permissions-panel',

	/** @type OrganizationTemplatePermissions */
	collection: null,

	events: {
		'click .add-permission': 'onClickAddPermission',
	},

	initialize (options) {
		this.options = options
		this.rollupTreeGroupsByTreeId = options.rollupTreeGroupsByTreeId || {}
		this.orgPermissionViews = []

		if (this.options.initial) {
			this.field = this.options.initial.field
			this.dataset = this.options.initial.dataset
			if (this.field?.treeId && this.options.initial.rollupTreeGroups) {
				this.rollupTreeGroupsByTreeId[this.field.treeId] =
					this.options.initial.rollupTreeGroups
			}
		}
	},

	render() {
		this.$el.empty().append(template())
		this.loadingDots = new LoadingDots({ el: this.$('.loading-dots-container') })

		this.updateVisibility()

		this.collection.forEach(p => this.renderOrgPermission(p))

		return this
	},

	/**
	 * Sets the dataset and field that is being used for the organization
	 * template. May result in panel visibility changes, changing the
	 * OrganizationTemplatePermissions collection, fetching tree items, etc
	 *
	 * @param {Dataset} dataset
	 * @param {Object} field
	 *
	 * @returns {Promise<void>} a promise that resolves when any async work
	 * (like fetching tree items) is complete
	 */
	async datasetAndField ({ dataset, field }) {
		let oldTreeId = this.field?.treeId

		this.dataset = dataset
		this.field = field

		this.updateVisibility()

		// Clear permissions collection if rollupTree changes
		if (oldTreeId != this.field?.treeId) {
			this.collection.set([])
			this.orgPermissionViews.forEach(v => v.remove())
			this.orgPermissionViews = []
		}

		await this.ensureRollupTreeGroups()
	},

	/**
	 * Ensures that the rollup tree groups for the rollup field for the
	 * currently selected dataset field are loaded. Will fetch them from the
	 * server if they are not loaded yet.
	 *
	 * @returns {Promise<void>} a promise that resolves when the dataset tree
	 * templates are loaded
	 */
	async ensureRollupTreeGroups () {
		if (!this.fieldIsRollup()) {
			return
		}

		if (!_.isNil(this.allRollupTreeGroups())) {
			return
		}

		this.setLoading(true)

		this.rollupTreeGroupsByTreeId[this.field.treeId] =
			await this.fetchRollupTreeGroups(this.field.treeId)

		this.setLoading(false)
	},

	async fetchRollupTreeGroups (datasetTreeId) {
		return new Promise(resolve => {
			$.get(`/api/dataset-trees/${datasetTreeId}?templates=true`, treeItem => {
				resolve(treeItem.templates ?? [])
			})
		})
	},

	fieldIsRollup () {
		let dataType = unwrapModel(this.field)?.dataType
		return _.isNil(dataType)
			? false
			: dataType == ROLLUP
	},

	updateVisibility () {
		// #55912 only show for rollup fields, not geo fields (which don't have
		// rollup tree templates)
		let show = this.fieldIsRollup()

		this.$el.toggleClass('is-not-displayed', !show)

		this.updateEmptyState()
	},

	updateEmptyState (forceShow) {
		let show = _.isNil(forceShow)
			? this.collection.length == 0
			: forceShow

		// Need to use the is-showing-empty-state class instead of just relying
		// on the "&:empty" rule, because loading-dots-container is always there
		this.$('.panel-content').toggleClass('is-showing-empty-state', show)
	},

	setLoading (loading) {
		if (loading) {
			this.loadingDots.render()
			// Force empty state not to show while loading, because we only
			// want the loading dots
			this.updateEmptyState(false)
		} else {
			this.loadingDots.clear()
			this.updateEmptyState()
		}

		this.$('.add-permission').toggleClass('is-not-displayed', loading)
	},

	/**
	 * Returns all rollup tree groups for the rollup field for the currently
	 * selected dataset field.
	 *
	 * @returns {rollupTreeGroup[]|null}
	 */
	allRollupTreeGroups () {
		if (!this.field?.treeId) {
			return null
		}

		return this.rollupTreeGroupsByTreeId[this.field.treeId]
	},

	onClickAddPermission () {
		this.addPermissionTooltip?.remove()

		let rollupTreeGroups = this.allRollupTreeGroups()
		let content, extraClass
		if (_.isEmpty(rollupTreeGroups)) {
			// If there are no rollup tree groups for this field, give them
			// a link to the admin screen to create them
			content = this.contentForAdminLink()
		} else {
			// Otherwise, give them a bootslide menu of templates to select
			content = new AddPermissionTooltip({
				rollupTreeGroups: this.allRollupTreeGroups()
			})
			content.on('add', template => this.addPermission(template))

			extraClass = 'has-bootslide-menu'
		}

		this.addPermissionTooltip = new Tooltip({
			attachTo: this.$('.add-permission'),
			content,
			placement: 'below',
			arrow: 'right',
			extraClass
		}).render()
	},

	contentForAdminLink () {
		let treeId = this.field?.treeId
		let link = adminLink.adminDatasetRollupTreesLink(treeId)
		return i18n.get(
			'admin.group.organizationPermissions.rollupTreeSetupInstructions',
			link)
	},

	addPermission (rollupTemplate) {
		// #55866
		// Handle specific default keys necessary for different customers
		let defaultType = groupType.defaultMessageKey()
		let orgPermission = new OrganizationTemplatePermission({
			organizationTemplateId: this.options.organizationTemplateId,
			rollupTreeGroupId: rollupTemplate.id,
			groupType: defaultType,
			// Note: intentionally leaving permissionIds null, so that
			// AdvancedPermissionsForm.setPermissionsForGroupType() will assign
			// default permissions
			permissionIds: null,
			cascade: true

		})
		this.collection.add(orgPermission)
		this.renderOrgPermission(orgPermission)
		this.updateVisibility()
	},

	renderOrgPermission (orgPermission) {
		let rollupTreeGroup = _.find(this.allRollupTreeGroups(),
				t => t.id == orgPermission.get('rollupTreeGroupId'))

		let view = new OrganizationTemplatePermissionView({
			model: orgPermission,
			rollupTreeGroup: rollupTreeGroup
		})

		this.orgPermissionViews.push(view)
		this.$('.panel-content').append(view.el)
		view.render()

		this.listenTo(view, 'delete',
			() => this.deleteOrgPermission(view))
		this.listenTo(view, 'change',
			() => this.trigger('change'))
	},

	deleteOrgPermission (view) {
		this.collection.remove(view.model)
		view.remove()
		this.orgPermissionViews = _.without(this.orgPermissionViews, view)
		this.updateVisibility()
		// #55976
		// Reset form state so components, like buttons, are re-enabled if an
		// error had occurred
		this.trigger('change')
	},

	/**
	 * Returns all data appropriate for when the user hits Next/Save
	 */
	gatherData () {
		this.collection.forEach(p => {
			let pruneModel = _.isNil(p.get('permissionIds'))
				// If permissionIds is null, then the user hasn't gone into the
				// "Actions" dialog. In that case, set the default permissions
				? null
				// If there are already permissions, passing in this third
				// argument will prune the permissions if the group type was
				// changed to a lower-access type
				: p

			setPermissionsForGroupType(
				p, p.get('groupType'), pruneModel)
		})

		return {
			organizationTemplatePermissions: this.collection,
			rollupTreeGroups: this.allRollupTreeGroups()
		}
	},

	autoRemove: true
})

/**
 * Tooltip that shows a list of rollup tree groups that can be used for a
 * new OrganizationTemplatePermission
 */
const AddPermissionTooltip = BootslideMenu.extend({
	allowDynamicWidth: true,

	menu: function() {

		let options = this.options.rollupTreeGroups.map(t => {
			let label= BootslideMenu.configIconLabel(
				'rollup-tree', _.escape(t.label))

			return {
				label,
				target: () => {
					this.trigger('add', t)
					this.trigger('done')
				}
			}
		})

		return {
			label: 'datasets.rollupTreeGroup.add',
			target: options
		}
	},
})

export default OrganizationTemplatePermissionsPanel

OrganizationTemplatePermissionsPanel.__filename__ = 'organizations/organization-template-permissions-panel.js'
